import { KatFileUpload, KatTooltip } from '@amzn/katal-react';
import React, { useContext } from 'react';
import styled from 'styled-components';
import {
  DEFAULT_ACCEPTED_FILES,
  FILE_EXTENSION,
  fileFormats,
  UPLOAD_MAX_FILE_SIZE,
} from 'src/constants/GlobalConstants';
import { UploaderConstants } from 'src/constants/ProjectConstants';
import { TranslationContext } from 'src/state/providers/TranslationProvider';
import { useLocalizationContext } from '@amzn/react-arb-tools';

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.Crimson};
  font-size: ${({ theme }) => theme.fontSize.xs};
  margin-top: 0.25rem;
`;

/**
 * Gets the MIME type for a given file extension from FILE_EXTENSION enum
 * @param extension The file extension from FILE_EXTENSION enum (e.g., FILE_EXTENSION.PDF)
 * @returns The corresponding MIME type string (e.g., 'application/pdf')
 * @example getMimeType(FILE_EXTENSION.PDF) // returns 'application/pdf'
 */
export const getMIMEType = (extension: FILE_EXTENSION): string | undefined => {
  return fileFormats[extension]?.type;
};

/**
 * Gets the file extension string for a given FILE_EXTENSION enum value
 * @param extension The file extension from FILE_EXTENSION enum (e.g., FILE_EXTENSION.PDF)
 * @returns The corresponding file extension string without dot (e.g., 'pdf')
 * @example getExtension(FILE_EXTENSION.PDF) // returns 'pdf'
 */
export const getExtension = (extension: FILE_EXTENSION): string | undefined => {
  return fileFormats[extension]?.extension;
};

/**
 * Checks whether the file type is valid or not
 * @param extension file extension eg: msg
 * @param allowedExtensions Array of allowed file extensions from FILE_EXTENSION enum (e.g., [FILE_EXTENSION.PDF, FILE_EXTENSION.TXT])
 * @returns boolean
 */
export function isValidFileType(
  extension: string | undefined,
  allowedExtensions: FILE_EXTENSION[] = DEFAULT_ACCEPTED_FILES,
) {
  if (!extension) {
    return false;
  }
  const normalizedExtension = extension.toUpperCase();

  return allowedExtensions.some((allowedExtension) => {
    const extractedExtension = getExtension(allowedExtension);
    if (!extractedExtension) {
      return false;
    }
    return extractedExtension.toUpperCase() === normalizedExtension;
  });
}

export interface UploaderProps extends Omit<KatFileUpload.Props, 'accept'> {
  allowedFiles?: FILE_EXTENSION[];
  errorMessage?: string;
}

const Uploader: React.FC<UploaderProps> = ({
  onFilesAttached,
  onFilesRemoved,
  onFilesReplaced,
  allowedFiles = DEFAULT_ACCEPTED_FILES,
  hint = UploaderConstants.DEFAULT_HINT,
  maxSize,
  required = false,
  variant = 'large',
  fileStates = [],
  constraintsMessage = UploaderConstants.UPLOADER_CONSTRAINT_MESSAGE,
  errorMessage,
}) => {
  const { fileUploaderBundle } = useContext(TranslationContext);
  const { localizationContext } = useLocalizationContext();
  const acceptFileExtensions = allowedFiles.map((ext) => getExtension(ext));
  const acceptFileTypes = allowedFiles.map((ext) => getMIMEType(ext)).join(',');

  return (
    <>
      <KatFileUpload
        file-view="list"
        multiple
        required={required}
        variant={variant}
        accept={acceptFileTypes}
        locale={localizationContext.getLocale()}
        maxSize={maxSize} // max size in bytes
        hint={fileUploaderBundle.getMessage(UploaderConstants.DEFAULT_HINT)}
        onFilesAttached={(e) => onFilesAttached!(e)}
        onFilesRemoved={(e) => onFilesRemoved!(e)}
        onFilesReplaced={(e) => onFilesReplaced!(e)}
        fileStates={{ fileItemUpdateStates: fileStates }}
        data-testid="correspondence-file-upload"
      >
        {constraintsMessage && (
          <div slot="constraints-message">
            {fileUploaderBundle.getMessage(
              UploaderConstants.UPLOADER_CONSTRAINT_MESSAGE,
            )}
            <KatTooltip
              label={`${fileUploaderBundle.getMessage(
                UploaderConstants.ALLOWED_FILETYPES,
              )} ${acceptFileExtensions.join(', ')}`}
              position="right"
              size="tiny"
              triggerIcon="help"
            />
          </div>
        )}
      </KatFileUpload>
      {errorMessage && (
        <ErrorMessage data-testid="file-upload-error-message">
          {errorMessage}
        </ErrorMessage>
      )}
    </>
  );
};

export default Uploader;
